import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { RootState } from "../../../../../Controllers/Store";
import { useSelector } from "react-redux";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";
import { Nullable } from "primereact/ts-helpers";
import { Toast } from "primereact/toast";
import { RadioButton, RadioButtonChangeEvent } from "primereact/radiobutton";
import { Calendar } from "primereact/calendar";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import DatePicker from "../../../../../Components/DatePicker";
import ErrorPage from "../../../../../Components/ErrorPage";
import Textarea from "../../../../../Components/TextArea";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";
import PageLoader from "../../../../../Components/PageLoader";
import GoBackButton from "../../../../../Components/GoBackButton";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { checkModelSelected, checkText } from "../../../../../Utils/Validator";
import { formatDate, formatTo12HourTime, parseStringToDate } from "../../../../../Utils/getDate";

import { SiteInspectionBooking as SiteInspectionBookingType } from "../../../../../Controllers/Types/SiteInspectionBooking/SiteInspectionBookingTypes";
import { InspectionTimeList } from "../../../../../Controllers/Types/SiteVisit/SiteVisitTypes";

import { fetchSiteInspectionBooking } from "../../../../../Controllers/Thunks/SiteInspectionBookingThunks";
import { getAllInspectionTimeList } from "../../../../../Controllers/Thunks/SiteVisitThunks";

import {
    createSiteInspectionBookingAPI,
    updateSiteInspectionBookingAPI,
    deleteSiteInspectionBookingAPI,
    acceptSiteInspectionBookingAPI
} from "../../../../../Api/SiteInspectionBooking/SiteInspectionBookingApiServices";
import { Tooltip } from "primereact/tooltip";


const SiteInspectionBooking: React.FC = () => {
    const today = new Date();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const calendarRef = useRef<Calendar>(null);
    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);
    const isShared: boolean = projectData?.sharedBy !== null;
    const canEdit: boolean = Boolean(projectData?.can_edit);

    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [siteInspectionBookingUpdateHasChanges, setSiteInspectionBookingUpdateHasChanges] = useState<boolean>(false);
    const [isChangeAcceptDate, setIsChangeAcceptDate] = useState<boolean>(false);

    const {
        inspectionTimeListLoading,
        inspectionTimeList,
        inspectionTimeListError
    } = useAppSelector((state) => state?.siteVisitList);

    const {
        loadingSiteInspectionBooking,
        errorSiteInspectionBooking,
        siteInspectionBookings
    } = useAppSelector((state) => state?.siteInspectionBooking);

    const [siteInspectionBookingsData, setSiteInspectionBookingsData] = useState<SiteInspectionBookingType[]>([]);
    const [selectedSiteInspectionBookingData, setSelectedSiteInspectionBookingData] = useState<SiteInspectionBookingType | null>(null);
    const [lastSiteInspectionBookingData, setLastSiteInspectionBookingData] = useState<SiteInspectionBookingType | null>(null);

    const [inspectionTimeListData, setInspectionTimeListData] = useState<InspectionTimeList[]>([]);
    const [siteInspectionBookingStatusFilterState, setSiteInspectionBookingStatusFilterState] = useState<'All' | 'Pending' | 'Accepted'>('All');

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
        };

        checkRole();
    }, []);
    /*  */

    /* Check permission */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionAddSiteInspectionBooking = useCanEdit("Site Inspection Booking", "add_site_inspection_booking");
    const hasPermissionEditSiteInspectionBooking = useCanEdit("Site Inspection Booking", "edit_site_inspection_booking");
    const hasPermissionDeleteSiteInspectionBooking = useCanEdit("Site Inspection Booking", "delete_site_inspection_booking");
    /*  */

    const checkAccessPermission = useCallback(
        (permission: boolean): boolean => {
            return isShared ? canEdit : permission;
        },
        [isShared, canEdit]
    );

    const [showSiteInspectionRequestModal, setShowSiteInspectionRequestModal] = useState<boolean>(false);
    const [showSiteInspectionResponseModal, setShowSiteInspectionResponseModal] = useState<boolean>(false);

    /* Site inspection booking request fields */
    const [siteInspectionRequestDate, setSiteInspectionRequestDate] = useState<Nullable<Date>>(today);
    const [siteInspectionRequestNote, setSiteInspectionRequestNote] = useState<string>('');

    /* Errors */
    const [siteInspectionRequestDateError, setSiteInspectionRequestDateError] = useState<string>('');
    const [siteInspectionRequestNoteError, setSiteInspectionRequestNoteError] = useState<string>('');

    /* Site inspection booking request fields */
    const [selectedInspectionTime, setSelectedInspectionTime] = useState<InspectionTimeList | null>(null);
    const [siteInspectionAcceptDate, setSiteInspectionAcceptDate] = useState<Nullable<Date>>(today);

    /* Errors */
    const [selectedInspectionTimeError, setSelectedInspectionTimeError] = useState<string>('');
    const [siteInspectionAcceptDateError, setSiteInspectionAcceptDateError] = useState<string>('');

    useEffect(() => {
        handleSiteInspectionBookingDataGet();
        dispatch(getAllInspectionTimeList());
    }, [dispatch, location?.state]);

    const handleSiteInspectionBookingDataGet = async () => {
        await dispatch(fetchSiteInspectionBooking(folderId));
        setInitialDataLoading(false);
    }

    useEffect(() => {
        if (siteInspectionBookings && siteInspectionBookings?.length > 0) {
            let filteredData: SiteInspectionBookingType[] = [];

            if (siteInspectionBookingStatusFilterState === 'All') {
                filteredData = siteInspectionBookings;
            } else {
                filteredData = siteInspectionBookings?.filter((data) => data.status === siteInspectionBookingStatusFilterState);
            }

            setSiteInspectionBookingsData(filteredData);
            setLastSiteInspectionBookingData(siteInspectionBookings[siteInspectionBookings.length - 1]);
        } else {
            setSiteInspectionBookingsData([]);
            setLastSiteInspectionBookingData(null);
        }

        if (inspectionTimeList && inspectionTimeList?.length > 0) {
            const activeList = inspectionTimeList?.filter((list) => list?.status === 1);
            setInspectionTimeListData(activeList);
            setSelectedInspectionTime(activeList[0]);
        } else {
            setInspectionTimeListData([]);
            setSelectedInspectionTime(null);
        }
    }, [siteInspectionBookingStatusFilterState, siteInspectionBookings, inspectionTimeList]);

    const handleFilterSiteInspectionBooking = (status: 'All' | 'Pending' | 'Accepted') => {
        setSiteInspectionBookingStatusFilterState(status);
    }

    /* *************************************************** Site inspection request */
    const handleResetSiteInspectionRequestFields = () => {
        setSiteInspectionRequestDate(today);
        setSiteInspectionRequestNote('');

        setSelectedSiteInspectionBookingData(null);
    }

    const handleCloseSiteInspectionBookingModal = () => {
        setShowSiteInspectionRequestModal(false);
        handleResetSiteInspectionRequestFields();
    }

    const handleRequestSiteInspectionBooking = () => {
        setDataState('Add');
        setShowSiteInspectionRequestModal(true);
    }

    /* Clear error messages */
    useEffect(() => {
        checkText(formatDate(siteInspectionRequestDate ?? null), false, setSiteInspectionRequestDateError);
    }, [siteInspectionRequestDate]);

    useEffect(() => {
        checkText(siteInspectionRequestNote, false, setSiteInspectionRequestNoteError);
    }, [siteInspectionRequestNote]);

    // Check credentials for site inspection request
    function checkCredentialsForSiteInspectionRequest(): boolean {
        const isValidRequestDate = checkText(formatDate(siteInspectionRequestDate ?? null), true, setSiteInspectionRequestDateError);
        const isValidRequestNote = checkText(siteInspectionRequestNote, true, setSiteInspectionRequestNoteError);

        return isValidRequestDate && isValidRequestNote;
    }

    // Get api error messages
    function getApiErrorSiteInspectionRequest(keyName: string, error: string): void {
        if (keyName === "date") {
            setSiteInspectionRequestDateError(error);
        }

        if (keyName === "note") {
            setSiteInspectionRequestNoteError(error);
        }
    }

    // Create site inspection request API
    const handleCreateSiteInspectionRequest = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForSiteInspectionRequest() || !folderId) return;

        setLoading(true);

        try {
            const folder_id: number = folderId;
            const status = 'Pending';
            const date: string = formatDate(siteInspectionRequestDate ?? null);
            const note: string = siteInspectionRequestNote;

            const data = await createSiteInspectionBookingAPI(
                folder_id,
                status,
                date,
                note,
            );

            if (data?.statusCode === 200) {
                handleSiteInspectionBookingDataGet();
                handleCloseSiteInspectionBookingModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: 'The site inspection booking was created and successfully sent to the building inspector.',
                    life: 4000,
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    getApiErrorSiteInspectionRequest(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the site inspection booking.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the site inspection booking:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the site inspection booking.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    // Edit site inspection booking
    const handleEditSiteInspectionBooking = (data: SiteInspectionBookingType) => {
        if (!data) return;

        setDataState('Edit');
        setSelectedSiteInspectionBookingData(data);
        setShowSiteInspectionRequestModal(true);

        setSiteInspectionRequestDate(data?.date ? parseStringToDate(data?.date) : null);
        setSiteInspectionRequestNote(data?.note!);
    }

    /* Check update has changes */
    useEffect(() => {
        if (!selectedSiteInspectionBookingData) return;

        if (formatDate(siteInspectionRequestDate!) !== selectedSiteInspectionBookingData?.date) {
            setSiteInspectionBookingUpdateHasChanges(true);
        } else if (siteInspectionRequestNote !== null && siteInspectionRequestNote !== selectedSiteInspectionBookingData?.note) {
            setSiteInspectionBookingUpdateHasChanges(true);
        } else {
            setSiteInspectionBookingUpdateHasChanges(false);
        }
    }, [selectedSiteInspectionBookingData, siteInspectionRequestDate, siteInspectionRequestNote]);


    // Update site inspection booking API
    const handleUpdateSiteInspectionBooking = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForSiteInspectionRequest() || !selectedSiteInspectionBookingData) return;

        setLoading(true);

        try {
            const site_inspection_booking_id: number = selectedSiteInspectionBookingData?.id!;
            const status = 'Pending';
            const date: string = formatDate(siteInspectionRequestDate!);
            const note: string = siteInspectionRequestNote;

            const data = await updateSiteInspectionBookingAPI(
                site_inspection_booking_id,
                status,
                date,
                note,
            );

            if (data?.statusCode === 200) {
                handleSiteInspectionBookingDataGet();
                handleCloseSiteInspectionBookingModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: 'The site inspection booking was updated successfully.',
                    life: 4000,
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    getApiErrorSiteInspectionRequest(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating the site inspection booking.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating the site inspection booking:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the site inspection booking.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    // Delete site inspection booking
    const handleDeleteSiteInspectionBooking = () => {
        if (!selectedSiteInspectionBookingData) return;

        if (selectedSiteInspectionBookingData) {
            confirmDialog({
                message: `Are you sure you want to delete this site inspection booking ${selectedSiteInspectionBookingData?.site_visit_no}?`,
                header: 'Confirmation',
                icon: 'bi bi-info-circle',
                defaultFocus: 'accept',
                acceptClassName: 'p-button-danger',
                accept: () => deleteSiteInspectionBooking(selectedSiteInspectionBookingData?.id!),
                dismissableMask: true,
                resizable: false,
                draggable: false,
            });
        }
    }

    // Delete site inspection booking API
    const deleteSiteInspectionBooking = async (siteInspectionBookingId: number) => {
        if (!siteInspectionBookingId) return;

        setDeleteLoading(true);

        try {
            const result = await deleteSiteInspectionBookingAPI(siteInspectionBookingId);

            if (result?.statusCode === 200) {
                handleSiteInspectionBookingDataGet();
                handleCloseSiteInspectionBookingModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Site visit inspection booking deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the site inspection booking.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the site inspection booking:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the site inspection booking.",
                life: 3000,
            });
        } finally {
            setDeleteLoading(false);
        }
    }

    const siteInspectionRequestModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState === 'Add' ? (
                    <React.Fragment>
                        Create site inspection booking&nbsp;
                        {lastSiteInspectionBookingData !== null ? (String(lastSiteInspectionBookingData?.site_visit_no + 1).padStart(2, '0')) : 1}
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        Edit site inspection booking&nbsp;
                        {selectedSiteInspectionBookingData !== null ? (String(selectedSiteInspectionBookingData?.site_visit_no).padStart(2, '0')) : ''}
                    </React.Fragment>
                )}
            </h5>

            {dataState === "Edit" && checkAccessPermission(hasPermissionDeleteSiteInspectionBooking) ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <button
                            type="button"
                            className="close_modal_btn p-ripple"
                            onClick={handleDeleteSiteInspectionBooking}
                        >
                            <i className="bi bi-trash3"></i>
                            <Ripple />
                        </button>
                    )}
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={handleCloseSiteInspectionBookingModal}
                >
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    );

    const siteInspectionRequestModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseSiteInspectionBookingModal}
            />

            <Button
                label={`${loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}`}
                onClick={dataState === "Add" ? handleCreateSiteInspectionRequest : handleUpdateSiteInspectionBooking}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !siteInspectionBookingUpdateHasChanges}
            />
        </div>
    );
    /* *************************************************** */


    /* *************************************************** Site inspection response */
    const handleResetSiteInspectionResponseFields = () => {
        setSiteInspectionAcceptDate(today);
        setSelectedInspectionTime(inspectionTimeListData[0]);

        setSelectedSiteInspectionBookingData(null);
    }

    const handleCloseSiteInspectionResponseModal = () => {
        setShowSiteInspectionResponseModal(false);
        handleResetSiteInspectionResponseFields();
    }

    const handleResponseSiteInspectionBooking = (data: SiteInspectionBookingType) => {
        if (!data) return;

        setShowSiteInspectionResponseModal(true);
        setSelectedSiteInspectionBookingData(data);
    }

    // Check credentials for site inspection response
    function checkCredentialsForSiteInspectionResponse(): boolean {
        const isValidResonseTime = checkModelSelected((selectedInspectionTime !== null), true, setSelectedInspectionTimeError);
        const isValidResonseDate = checkText(formatDate(siteInspectionAcceptDate ?? null), true, setSiteInspectionAcceptDateError);

        return isValidResonseTime && isValidResonseDate;
    }

    // Get api error messages
    function getApiErrorSiteInspectionResponse(keyName: string, error: string): void {
        if (keyName === "time_id") {
            setSelectedInspectionTimeError(error);
        }

        if (keyName === "date") {
            setSiteInspectionAcceptDateError(error);
        }
    }

    const handleAcceptSiteInspectionBooking = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForSiteInspectionResponse() || !selectedSiteInspectionBookingData) return;

        setLoading(true);

        try {
            const site_inspection_booking_id: number = selectedSiteInspectionBookingData?.id!;
            const time_id: number = selectedInspectionTime?.id!;
            const date: string = formatDate(siteInspectionAcceptDate!);

            const data = await acceptSiteInspectionBookingAPI(
                site_inspection_booking_id,
                time_id,
                date
            )

            if (data?.statusCode === 200) {
                handleSiteInspectionBookingDataGet();
                handleCloseSiteInspectionResponseModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `The request for the site inspection booking ${String(selectedSiteInspectionBookingData?.site_visit_no).padStart(2, '0')} was successfully accepted.`,
                    life: 4000,
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    getApiErrorSiteInspectionResponse(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: `There was an error accepting the site inspection booking ${String(selectedSiteInspectionBookingData?.site_visit_no).padStart(2, '0')}.`,
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error accepting the site inspection booking:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while accepting the site inspection booking.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const siteInspectionBookingResponseModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Response to site inspection booking {String(selectedSiteInspectionBookingData?.site_visit_no).padStart(2, '0')}
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseSiteInspectionResponseModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    const siteInspectionBookingResponseModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label={loading ? 'Processing' : 'Accept'}
                onClick={handleAcceptSiteInspectionBooking}
                loading={loading}
                className="custom_btn primary"
            />
        </div>
    );

    const handleToggleChangeAcceptDate = () => {
        const next = !isChangeAcceptDate;
        setIsChangeAcceptDate(next);

        if (!isChangeAcceptDate) {
            setTimeout(() => {
                calendarRef.current?.show();
            }, 0);
        }
    };
    /* *************************************************** */


    const tableActionBody = (rowData: SiteInspectionBookingType) => {
        const isSiteInspectionInPending: boolean = rowData?.status === 'Pending';

        return (
            <React.Fragment>
                {isArchitect ? (
                    <div className={`table_action_btn_area justify-content-end mt-1 mb-1`}>
                        {checkAccessPermission(hasPermissionEditSiteInspectionBooking) && (
                            <button
                                className="table_action_btn fixed_width p-ripple"
                                onClick={() => handleEditSiteInspectionBooking(rowData)}
                                disabled={!isSiteInspectionInPending}
                            >
                                Edit
                                <Ripple />
                            </button>
                        )}
                    </div>
                ) : isInspector ? (
                    <div className={`table_action_btn_area justify-content-end mt-1 mb-1`}>
                        <button
                            className="table_action_btn fixed_width p-ripple"
                            onClick={() => handleResponseSiteInspectionBooking(rowData)}
                            disabled={!isSiteInspectionInPending}
                        >
                            {isSiteInspectionInPending ? 'Response' : 'Responsed'}
                            <Ripple />
                        </button>
                    </div>
                ) : null}
            </React.Fragment>
        )
    }

    const statusBody = (rowData: SiteInspectionBookingType) => {
        const status = rowData.status;
        let color = "";
        let bgColor = "";

        if (status === 'Accepted') {
            color = "#06b500";
            bgColor = "#d5f8df";
        } else if (status === 'Pending') {
            color = "#de9725";
            bgColor = "#f8f0d6";
        } else {
            color = "#656565";
            bgColor = "#EEE";
        }

        return (
            <div className="flex_center">
                <span className="priority_label lg" style={{ color: color, backgroundColor: bgColor }}>
                    {status}
                </span>
            </div>
        );
    };

    const handleCopyInspectionNote = async (note: string | null | undefined) => {
        if (!note) return;

        try {
            await navigator.clipboard.writeText(note);

            toast?.current?.show({
                severity: "success",
                summary: "Copied",
                detail: "Inspection note copied to clipboard",
                life: 2000,
            });
        } catch (error) {
            toast?.current?.show({
                severity: "error",
                summary: "Failed",
                detail: "Failed to copy inspection note",
                life: 2000,
            });
        }
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {(loadingSiteInspectionBooking && initialDataLoading) ? (
                    <PageLoader />
                ) : errorSiteInspectionBooking ? (
                    <ErrorPage errorMessage={errorSiteInspectionBooking} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData?.job_number ?? '---'} : {projectData?.address ?? '---'}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData?.folder ?? '---'}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {projectData?.feature_name ?? '---'}
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>
                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="contact_tabs">
                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width
                                    ${siteInspectionBookingStatusFilterState === "All" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteInspectionBooking('All')}
                                        disabled={siteInspectionBookings?.length === 0}
                                    >
                                        All : {String(siteInspectionBookings?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                            ${siteInspectionBookingStatusFilterState === "Pending" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteInspectionBooking("Pending")}
                                        disabled={siteInspectionBookings?.length === 0}
                                    >
                                        Pending : {String(siteInspectionBookings?.filter(data => data?.status === 'Pending')?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>

                                <div className="contact_tab_item">
                                    <button
                                        type="button"
                                        className={`contact_tab_btn fixed_min_width 
                                    ${siteInspectionBookingStatusFilterState === "Accepted" ? "active" : ""} p-ripple`}
                                        onClick={() => handleFilterSiteInspectionBooking("Accepted")}
                                        disabled={siteInspectionBookings?.length === 0}
                                    >
                                        Accepted : {String(siteInspectionBookings?.filter(data => data?.status === 'Accepted')?.length).padStart(2, '0')}
                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                    </button>
                                </div>
                            </div>

                            {isArchitect && checkAccessPermission(hasPermissionAddSiteInspectionBooking) && (
                                <div className="page_action_area">
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleRequestSiteInspectionBooking}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Create new booking</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section no_header">
                            {siteInspectionBookingsData && siteInspectionBookingsData?.length > 0 ? (
                                <DataTable
                                    value={siteInspectionBookingsData}
                                    paginator
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    size="small"
                                    tableStyle={{ minWidth: "50rem" }}
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    scrollable
                                    loading={loadingSiteInspectionBooking}
                                >
                                    <Column
                                        header="Inspection booking no."
                                        headerClassName="text_purple"
                                        body={(rowData: SiteInspectionBookingType) => (
                                            <span className="text_bold text_no_wrap">
                                                {rowData.site_visit_no ? ('Site inspection ' + String(rowData.site_visit_no).padStart(2, '0')) : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    {siteInspectionBookingStatusFilterState === 'All' && (
                                        <Column
                                            header="Status"
                                            alignHeader={"center"}
                                            headerClassName="text_purple"
                                            body={statusBody}
                                            style={{ width: "10%" }}
                                        ></Column>
                                    )}

                                    <Column
                                        header="Request Date"
                                        headerClassName="text_purple "
                                        body={(rowData: SiteInspectionBookingType) => (
                                            <span className="text_no_wrap">
                                                {rowData.date ? rowData.date : "---"}
                                            </span>
                                        )}
                                        style={{ width: "15%" }}
                                    ></Column>

                                    <Column
                                        header="Inspection date & time"
                                        headerClassName="text_purple"
                                        body={(rowData: SiteInspectionBookingType) => (
                                            rowData.time_id && rowData?.bi_date ? (
                                                <React.Fragment>
                                                    <span className="text_no_wrap">
                                                        {rowData.bi_date}
                                                    </span>
                                                    <br />
                                                    <small className="text-secondary text_no_wrap">
                                                        {formatTo12HourTime(rowData?.start_time)}
                                                        &nbsp;&nbsp;-&nbsp;&nbsp;
                                                        {formatTo12HourTime(rowData?.end_time)}
                                                    </small>
                                                </React.Fragment>
                                            ) : (
                                                <span className="text-secondary">
                                                    ----
                                                </span>
                                            )
                                        )}
                                        style={{ width: "20%" }}
                                    ></Column>

                                    <Column
                                        header="Inspection note"
                                        headerClassName="text_purple"
                                        field="note"
                                        body={(rowData: SiteInspectionBookingType) => (
                                            <>
                                                <Tooltip target={`.inspectionNote${rowData?.id}`} />
                                                <span className="position-relative me-2">
                                                    {rowData.note ? rowData.note : "---"}
                                                </span>

                                                {rowData.note && (
                                                    <i
                                                        className={`bi bi-copy cursor_pointer text_purple inspectionNote${rowData?.id}`}
                                                        data-pr-tooltip="Click to copy"
                                                        data-pr-position="top"
                                                        onClick={() => handleCopyInspectionNote(rowData.note)}
                                                    ></i>
                                                )}
                                            </>
                                        )}
                                        style={{ width: "30%" }}
                                    />

                                    <Column
                                        body={tableActionBody}
                                        className="table_btn_area"
                                        style={{ width: "10%" }}
                                        frozen={window.innerWidth > 575}
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${siteInspectionBookingStatusFilterState === 'All' ? '' : (siteInspectionBookingStatusFilterState).toLowerCase()} site inspection bookings found. 
                                    ${(checkAccessPermission(hasPermissionAddSiteInspectionBooking) && isArchitect && siteInspectionBookingStatusFilterState === 'All') ? 'Click the Add button to create the site inspection booking.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Site inspection request modal */}
            <Dialog
                visible={showSiteInspectionRequestModal}
                header={siteInspectionRequestModalHeader}
                footer={siteInspectionRequestModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSiteInspectionBookingModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pt-sm-2 pb-sm-0 w-100">
                        <div className="row">
                            {/* Date */}
                            <div className="col-12">
                                <DatePicker
                                    key={`siteInspectionRequestDate`}
                                    id="siteInspectionRequestDate"
                                    label="Date"
                                    labelHtmlFor="siteInspectionRequestDate"
                                    required={true}
                                    value={siteInspectionRequestDate}
                                    minDate={today}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setSiteInspectionRequestDate}
                                    error={siteInspectionRequestDateError}
                                    showClear
                                    onClear={() => setSiteInspectionRequestDate(null)}
                                />
                            </div>

                            {/* Note */}
                            <div className="col-12">
                                <Textarea
                                    key={`siteInspectionRequestNote`}
                                    id="siteInspectionRequestNote"
                                    label="Note"
                                    labelHtmlFor="siteInspectionRequestNote"
                                    required={true}
                                    value={siteInspectionRequestNote}
                                    placeholder="Note"
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSiteInspectionRequestNote(e.target.value)}
                                    error={siteInspectionRequestNoteError}
                                    formGroupClassName="mb-3"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}

            {/* Site inspection booking response modal */}
            <Dialog
                visible={showSiteInspectionResponseModal}
                header={siteInspectionBookingResponseModalHeader}
                footer={siteInspectionBookingResponseModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseSiteInspectionResponseModal}
                dismissableMask>
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pt-sm-2 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                <div className="data_display_area">
                                    <div className="data_display_grp">
                                        <h6 className="data_display_label">Note: </h6>
                                        <p>{selectedSiteInspectionBookingData?.note ? selectedSiteInspectionBookingData?.note : '----'}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Date */}
                            <div className="col-12">
                                <div className="accept_date_container">
                                    <DatePicker
                                        ref={calendarRef}
                                        key={`siteInspectionAcceptDate`}
                                        id="siteInspectionAcceptDate"
                                        label="Date"
                                        labelHtmlFor="siteInspectionAcceptDate"
                                        required={false}
                                        value={siteInspectionAcceptDate}
                                        minDate={today}
                                        placeholder="dd/mm/yyyy"
                                        onChange={setSiteInspectionAcceptDate}
                                        error={siteInspectionAcceptDateError}
                                        inputDisabled={!isChangeAcceptDate}
                                    />

                                    <button className={`is_btn date_change_btn ${isChangeAcceptDate ? 'danger' : 'primary'}`}
                                        onClick={handleToggleChangeAcceptDate}>
                                        {isChangeAcceptDate ? 'Cancel' : 'Change'}
                                    </button>
                                </div>
                            </div>

                            {/* Visit time */}
                            <div className="col-12">
                                {inspectionTimeListLoading ? (
                                    <div className="position-relative" style={{ minHeight: '100px' }}>
                                        <CustomTableLoader />
                                    </div>
                                ) : inspectionTimeListError ? (
                                    <div className="empty_inspection_area">
                                        <p><i className="bi bi-exclamation-circle me-1"></i>
                                            There was an error retrieving inspection time data!
                                        </p>
                                    </div>
                                ) : (
                                    <React.Fragment>
                                        {inspectionTimeListData && inspectionTimeListData?.length > 0 && (
                                            <div className="visit_time_area">
                                                {inspectionTimeListData?.map((time) => (
                                                    <div className="visit_time_container" key={time?.id}>
                                                        <div className="visit_time_check_grp">
                                                            <RadioButton
                                                                inputId={time?.name}
                                                                name="selectedInspectionTime"
                                                                value={selectedInspectionTime}
                                                                onChange={(e: RadioButtonChangeEvent) => setSelectedInspectionTime(time)}
                                                                checked={selectedInspectionTime?.id === time?.id}
                                                            />
                                                            <label htmlFor={time?.name} className="ml-2">{time?.name}</label>
                                                        </div>
                                                        <div className="visit_time_grp">
                                                            <span>{formatTo12HourTime(time?.start_time)}&nbsp;-&nbsp;{formatTo12HourTime(time?.end_time)}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        {selectedInspectionTimeError &&
                                            <small className="form_error_msg">{selectedInspectionTimeError}</small>
                                        }
                                    </React.Fragment>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}
        </React.Fragment>
    )
}

export default SiteInspectionBooking;